import { inject, Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AppsyncService } from './appsync.service';
import { DocumentNode } from 'graphql';
import { TC_CLAIMS_HEADER } from '../constants/auth-constants';
import { AuthApiService } from './api/auth-api.service';
import { FetchPolicy } from '@apollo/client/core';

@Injectable({
  providedIn: 'root',
})
export class BaseAppSyncService {
  protected _api: AppsyncService = inject(AppsyncService);
  protected _authApi: AuthApiService = inject(AuthApiService);

  /**
   * Helper function that abstracts away the underlying query logic needed and performs the query
   * @param graphql - graphql DocumentNode object for the query (see graphql-tag for help)
   * @param variables - object of variables that you want to pass to the query, key in this object must match the name of the graphql variable you want to match
   * @param builderFn - function that will build the returned object into an appropriate type rather than the generic T - sometimes needs to be passed via arrow function to preserve `this`
   * @param graphqlQueryName - name of the graphql query, needed to parse it from the graphql response
   * @param fetchPolicy - graphQL apollo fetchPolicy, defaults to no-cache
   * @returns - Observable with the return type of builderFn
   */
  protected query<T>(
    graphql: DocumentNode,
    variables: object,
    builderFn: (input: any) => T,
    graphqlQueryName: string,
    fetchPolicy: FetchPolicy = 'no-cache'
  ): Observable<T> {
    return from(this._api.gqlClient())
      .pipe(
        switchMap((client) => {
          return client.query({
            query: graphql,
            variables: variables,
            context: {
              headers: {
                [TC_CLAIMS_HEADER]: this._authApi.getUserClaimsHeader(),
              },
            },
            fetchPolicy: fetchPolicy,
          });
        })
      )
      .pipe(
        map((data: any) => {
          // if (!data.data[graphqlQueryName]) {
          //   console.warn(
          //     `Got ${data.data[graphqlQueryName]} when attempting to retrieve interior graphql response for query name: ${graphqlQueryName}, double check capitalization and spelling, full response follows`,
          //     data
          //   );
          // }
          return builderFn(data.data[graphqlQueryName]);
        })
      );
  }

  /**
   * Helper function that abstracts away the underlying mutation logic needed and performs the mutation
   * @param graphql - graphql DocumentNode object for the mutation (see graphql-tag for help)
   * @param variables - object of variables that you want to pass to the mutation, key in this object must match the name of the graphql variable you want to match
   * @param builderFn - function that will build the returned object into an appropriate type rather than the generic T - sometimes needs to be passed via arrow function to preserve `this`
   * @param graphqlMutationName - name of the graphql mutation, needed to parse it from the graphql response
   * @returns - Observable with the return type of builderFn
   */
  protected mutate<T>(
    graphql: DocumentNode,
    variables: object,
    builderFn: (input: any) => T,
    graphqlMutationName: string
  ): Observable<T> {
    return from(this._api.gqlClient())
      .pipe(
        switchMap((client) => {
          return client.mutate({
            mutation: graphql,
            variables: variables,
            context: {
              headers: {
                [TC_CLAIMS_HEADER]: this._authApi.getUserClaimsHeader(),
              },
            },
            fetchPolicy: 'no-cache',
          });
        })
      )
      .pipe(
        map((data: any) => {
          // if (!data.data[graphqlMutationName]) {
          //   console.warn(
          //     `Got ${data.data[graphqlMutationName]} when attempting to retrieve interior graphql response for mutation name: ${graphqlMutationName}, double check capitalization and spelling, full response follows`,
          //     data
          //   );
          // }
          return builderFn(data.data[graphqlMutationName]);
        })
      );
  }
}
